import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { isEnteralItem, getItemRoute, calculateRouteSplits, getRouteClinicalBadge } from './routeClassification';

export const generatePDF = async (record) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let yPosition = 15;
  
  // Get current date for generation timestamp
  const generateDate = new Date().toLocaleDateString('id-ID', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const generateTime = new Date().toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit'
  });
  
  // Parse record date
  const recordDate = new Date(record.date);
  const formattedRecordDate = recordDate.toLocaleDateString('id-ID', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Separate oral vs enteral items
  const allOralEnteralItems = record.oral_enteral_items || [];
  const oralOnlyItems = allOralEnteralItems.filter(item => !isEnteralItem(item));
  const enteralOnlyItems = allOralEnteralItems.filter(item => isEnteralItem(item));
  const parenteralItems = record.parenteral_items || [];

  // Calculate route splits & grand total
  const routeSplits = calculateRouteSplits(allOralEnteralItems, parenteralItems);
  const targetEnergy = record.target_energy_kcal || 1600;
  const grandTotalEnergy = routeSplits.totalKcal;
  const energyPctOfTarget = targetEnergy > 0 ? ((grandTotalEnergy / targetEnergy) * 100).toFixed(1) : '0.0';
  const numericPct = parseFloat(energyPctOfTarget);

  // Clinical status determination
  let clinicalStatusBadge = 'Target Tercapai (Adekuat)';
  let clinicalColor = [16, 185, 129]; // Emerald
  if (numericPct > 120) {
    clinicalStatusBadge = 'Waspada Overfeeding (>120%)';
    clinicalColor = [245, 158, 11]; // Amber/Orange
  } else if (numericPct < 50) {
    clinicalStatusBadge = 'Defisit Berat / Malnutrisi (<50%)';
    clinicalColor = [225, 29, 72]; // Rose
  } else if (numericPct < 80) {
    clinicalStatusBadge = 'Defisit Ringan-Sedang (50-79%)';
    clinicalColor = [217, 119, 6]; // Amber
  }
  
  // ===== 1. HEADER DOKUMEN RESUME MEDIS GIZI =====
  pdf.setFillColor(5, 150, 105); // Emerald-600
  pdf.rect(0, 0, pageWidth, 28, 'F');
  
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(15);
  pdf.setFont('helvetica', 'bold');
  pdf.text('NUTRIMUS CLINICAL NUTRITION APP', 14, 11);
  
  pdf.setFontSize(10.5);
  pdf.setFont('helvetica', 'bold');
  pdf.text('RESUME MEDIS ASUHAN GIZI KLINIS HARIAN', 14, 18);
  
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Clinical Nutrition Assessment & Daily Intake Monitoring Report', 14, 23);

  // Right side header metadata
  pdf.setFontSize(8);
  pdf.text(`Tgl Cetak: ${generateDate}, ${generateTime}`, pageWidth - 14, 13, { align: 'right' });
  pdf.text(`Halaman 1 dari Rekam Asupan`, pageWidth - 14, 19, { align: 'right' });
  pdf.text('Kerahasiaan Medis (Medical Confidential)', pageWidth - 14, 24, { align: 'right' });
  
  yPosition = 33;
  
  // ===== 2. KARTU IDENTITAS PASIEN & EVALUASI KLINIS =====
  const patientDetails = record.patient || {};
  const patientName = record.patient_name || patientDetails.name || 'Pasien';
  const age = record.age || patientDetails.age || '-';
  const gender = record.gender || patientDetails.gender || '-';
  const genderLabel = gender === 'male' ? 'Laki-laki' : gender === 'female' ? 'Perempuan' : gender;

  // Box background
  pdf.setFillColor(248, 250, 252); // Slate-50
  pdf.setDrawColor(203, 213, 225); // Slate-300
  pdf.setLineWidth(0.3);
  pdf.roundedRect(14, yPosition, pageWidth - 28, 24, 2, 2, 'FD');

  // Text inside identity card
  pdf.setTextColor(15, 23, 42); // Slate-900
  pdf.setFontSize(9.5);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Nama Pasien: ${patientName}`, 18, yPosition + 6);
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(51, 65, 85);
  pdf.text(`Tanggal Evaluasi: ${formattedRecordDate}`, 120, yPosition + 6);

  pdf.text(`Usia / Jenis Kelamin: ${age} thn / ${genderLabel}`, 18, yPosition + 12);
  
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(5, 150, 105);
  pdf.text(`Target Energi: ${targetEnergy} Kcal`, 120, yPosition + 12);
  
  // Status Clinical Badge Pill in box
  pdf.setTextColor(clinicalColor[0], clinicalColor[1], clinicalColor[2]);
  pdf.text(`Capaian Asupan: ${Math.round(grandTotalEnergy)} Kcal (${energyPctOfTarget}%) • ${clinicalStatusBadge}`, 18, yPosition + 19);

  // Generous breathing room between patient card and section 1
  yPosition += 33;

  // ===== 3. TABEL KOMPARASI RUTE ASUPAN (ORAL vs ENTERAL vs PARENTERAL) =====
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(15, 23, 42);
  pdf.text('1. Komparasi Rute Asupan Nutrisi (Route Intake Breakdown)', 14, yPosition);
  yPosition += 5;

  const routeSummaryBody = [
    [
      'Asupan Oral',
      `${routeSplits.oralKcal} Kcal`,
      `${targetEnergy > 0 ? ((routeSplits.oralKcal / targetEnergy) * 100).toFixed(1) : 0}%`,
      `${routeSplits.oralPct.toFixed(1)}%`,
      'Makanan biasa / lunak per-oral'
    ],
    [
      'Asupan Enteral',
      `${routeSplits.enteralKcal} Kcal`,
      `${targetEnergy > 0 ? ((routeSplits.enteralKcal / targetEnergy) * 100).toFixed(1) : 0}%`,
      `${routeSplits.enteralPct.toFixed(1)}%`,
      'Formula sonde / NGT / ONS'
    ],
    [
      'Asupan Parenteral',
      `${routeSplits.parenteralKcal} Kcal`,
      `${targetEnergy > 0 ? ((routeSplits.parenteralKcal / targetEnergy) * 100).toFixed(1) : 0}%`,
      `${routeSplits.parenteralPct.toFixed(1)}%`,
      'Infus intravena perifer / sentral'
    ],
    [
      'TOTAL ASUPAN',
      `${Math.round(grandTotalEnergy)} Kcal`,
      `${energyPctOfTarget}%`,
      '100.0%',
      `Status: ${clinicalStatusBadge}`
    ]
  ];

  pdf.autoTable({
    startY: yPosition,
    head: [['Jalur / Rute Nutrisi', 'Energi (Kcal)', '% Target Kebutuhan', '% Proporsi Total', 'Karakteristik Jalur']],
    body: routeSummaryBody,
    theme: 'grid',
    headStyles: {
      fillColor: [16, 185, 129], // Emerald-500
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8.5,
      halign: 'center'
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 2.2
    },
    columnStyles: {
      0: { cellWidth: 42, fontStyle: 'bold' },
      1: { cellWidth: 30, halign: 'center' },
      2: { cellWidth: 35, halign: 'center' },
      3: { cellWidth: 30, halign: 'center' },
      4: { cellWidth: 'auto' }
    },
    didParseCell: function(data) {
      if (data.row.index === 3) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [241, 245, 249]; // Slate-100
      }
    },
    margin: { left: 14, right: 14 }
  });

  yPosition = pdf.lastAutoTable.finalY + 8;

  // ===== 4. TABEL RINCIAN ASUPAN PER RUTE =====
  // Helper for item tables
  const renderItemTable = (title, items, isParenteral = false, headerColor = [5, 150, 105]) => {
    if (!items || items.length === 0) return;

    // Check if enough vertical space left, else page break
    if (yPosition > pageHeight - 55) {
      pdf.addPage();
      yPosition = 18;
    }

    pdf.setFontSize(10.5);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(15, 23, 42);
    pdf.text(title, 14, yPosition);
    yPosition += 4;

    const tableData = items.map(item => [
      item.name,
      `${item.quantity} ${item.unit || (isParenteral ? 'ml' : 'porsi')}`,
      Math.round(item.energy_kcal).toString(),
      (item.protein_g || 0).toFixed(1),
      (item.carbohydrate_g || 0).toFixed(1),
      (item.fat_g || 0).toFixed(1)
    ]);

    const subtotalEnergy = items.reduce((s, i) => s + (parseFloat(i.energy_kcal) || 0), 0);
    const subtotalProtein = items.reduce((s, i) => s + (parseFloat(i.protein_g) || 0), 0);
    const subtotalCarbs = items.reduce((s, i) => s + (parseFloat(i.carbohydrate_g) || 0), 0);
    const subtotalFat = items.reduce((s, i) => s + (parseFloat(i.fat_g) || 0), 0);

    tableData.push([
      'Subtotal',
      `${items.length} item`,
      Math.round(subtotalEnergy).toString(),
      subtotalProtein.toFixed(1),
      subtotalCarbs.toFixed(1),
      subtotalFat.toFixed(1)
    ]);

    pdf.autoTable({
      startY: yPosition,
      head: [['Item / Produk', 'Jumlah', 'Energi (Kcal)', 'Protein (g)', 'KH (g)', 'Lemak (g)']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: headerColor,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 7.5,
        cellPadding: 1.8
      },
      columnStyles: {
        0: { cellWidth: 70, halign: 'left' },
        1: { cellWidth: 26, halign: 'center' },
        2: { cellWidth: 26, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' },
        4: { cellWidth: 20, halign: 'center' },
        5: { cellWidth: 20, halign: 'center' }
      },
      didParseCell: function(data) {
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [248, 250, 252];
        }
      },
      margin: { left: 14, right: 14 }
    });

    yPosition = pdf.lastAutoTable.finalY + 6;
  };

  // 4a. Makanan Oral
  if (oralOnlyItems.length > 0) {
    renderItemTable('2. Asupan Makanan Oral', oralOnlyItems, false, [16, 185, 129]);
  }

  // 4b. Formula Enteral
  if (enteralOnlyItems.length > 0) {
    renderItemTable('3. Asupan Formula Enteral (Sonde / Cair)', enteralOnlyItems, false, [2, 132, 199]);
  }

  // 4c. Nutrisi Parenteral
  if (parenteralItems.length > 0) {
    renderItemTable('4. Asupan Nutrisi Parenteral & Infus', parenteralItems, true, [147, 51, 234]);
  }

  // ===== 5. GRAND TOTAL MAKRONUTRIEN & ELEKTROLIT BOX =====
  if (yPosition > pageHeight - 55) {
    pdf.addPage();
    yPosition = 18;
  }

  const grandTotalData = record.grand_totals || record.grand_total || {};
  const grandTotalProtein = grandTotalData.protein_g || (routeSplits.oralProtein + routeSplits.enteralProtein + routeSplits.parenteralProtein);
  const grandTotalCarbs = grandTotalData.carbohydrate_g || (routeSplits.oralCarbs + routeSplits.enteralCarbs + routeSplits.parenteralCarbs);
  const grandTotalFat = grandTotalData.fat_g || (routeSplits.oralFat + routeSplits.enteralFat + routeSplits.parenteralFat);

  const proteinEnergy = grandTotalProtein * 4;
  const carbEnergy = grandTotalCarbs * 4;
  const fatEnergy = grandTotalFat * 9;
  const calcTotalEnergy = proteinEnergy + carbEnergy + fatEnergy || grandTotalEnergy || 1;

  const proteinPct = ((proteinEnergy / calcTotalEnergy) * 100).toFixed(1);
  const carbsPct = ((carbEnergy / calcTotalEnergy) * 100).toFixed(1);
  const fatPct = ((fatEnergy / calcTotalEnergy) * 100).toFixed(1);

  // Electrolyte calculations
  let totalSodiumMeq = 0;
  let totalPotassiumMeq = 0;
  allOralEnteralItems.forEach(item => {
    if (item.sodium_mg) totalSodiumMeq += item.sodium_mg / 23;
    if (item.potassium_mg) totalPotassiumMeq += item.potassium_mg / 39.1;
  });
  parenteralItems.forEach(item => {
    if (item.sodium_mmol) totalSodiumMeq += item.sodium_mmol;
    if (item.potassium_mmol) totalPotassiumMeq += item.potassium_mmol;
  });

  // Left side: Grand Total Summary Box
  const summaryBoxWidth = 105;
  const summaryBoxHeight = 44;
  pdf.setDrawColor(203, 213, 225);
  pdf.setFillColor(248, 250, 252);
  pdf.roundedRect(14, yPosition, summaryBoxWidth, summaryBoxHeight, 2, 2, 'FD');

  // Summary box header
  pdf.setFillColor(15, 23, 42);
  pdf.roundedRect(14, yPosition, summaryBoxWidth, 7, 2, 2, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('RINGKASAN TOTAL ASUPAN & MAKRONUTRIEN', 17, yPosition + 5);

  pdf.setTextColor(15, 23, 42);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  let lineY = yPosition + 13;
  pdf.text(`• Total Energi: ${Math.round(grandTotalEnergy)} Kcal (${energyPctOfTarget}% dari target)`, 17, lineY);
  lineY += 6;
  pdf.text(`• Protein: ${grandTotalProtein.toFixed(1)}g (${proteinPct}% kalori)`, 17, lineY);
  lineY += 6;
  pdf.text(`• Karbohidrat: ${grandTotalCarbs.toFixed(1)}g (${carbsPct}% kalori)`, 17, lineY);
  lineY += 6;
  pdf.text(`• Lemak: ${grandTotalFat.toFixed(1)}g (${fatPct}% kalori)`, 17, lineY);
  lineY += 6;
  pdf.text(`• Natrium: ${totalSodiumMeq.toFixed(1)} mEq | Kalium: ${totalPotassiumMeq.toFixed(1)} mEq`, 17, lineY);

  // Right side: Tanda Tangan & Verifikasi DPJP Box
  const signBoxX = 125;
  const signBoxWidth = pageWidth - 14 - signBoxX;
  pdf.setTextColor(15, 23, 42);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`${generateDate}`, signBoxX, yPosition + 4);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Dokter Spesialis Gizi Klinik', signBoxX, yPosition + 9);
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  pdf.setTextColor(100, 116, 139);
  pdf.text('(Tanda tangan & stempel verifikasi)', signBoxX, yPosition + 14);

  // Line for signature
  pdf.setDrawColor(148, 163, 184);
  pdf.setLineWidth(0.5);
  pdf.line(signBoxX, yPosition + 35, signBoxX + signBoxWidth, yPosition + 35);
  
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(15, 23, 42);
  pdf.text('(                                                                      )', signBoxX, yPosition + 39);
  pdf.setFontSize(7.5);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(100, 116, 139);
  pdf.text('SIP / NIP: .................................................', signBoxX, yPosition + 43);

  // ===== 6. HALAMAN LANJUTAN: MIKRONUTRIEN LENGKAP =====
  pdf.addPage();
  
  pdf.setFillColor(5, 150, 105);
  pdf.rect(0, 0, pageWidth, 16, 'F');
  pdf.setTextColor(255, 255, 255);
  
  // Baris 1: Judul Lampiran & Penomoran Halaman
  pdf.setFontSize(9.5);
  pdf.setFont('helvetica', 'bold');
  pdf.text('LAMPIRAN: KOMPOSISI LENGKAP MAKRONUTRIEN & MIKRONUTRIEN', 14, 6.5);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Resume Medis Gizi • Halaman 2', pageWidth - 14, 6.5, { align: 'right' });
  
  // Baris 2: Informasi Pasien & Tanggal Evaluasi
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Pasien: ${patientName}   |   Tanggal Evaluasi: ${formattedRecordDate}`, 14, 12);
  pdf.setFontSize(7.5);
  pdf.text('Kerahasiaan Medis (Medical Confidential)', pageWidth - 14, 12, { align: 'right' });
  
  yPosition = 23;
  pdf.setTextColor(15, 23, 42);
  pdf.setFontSize(9.5);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Rincian Vitamin, Mineral, dan Elektrolit', 14, yPosition);
  yPosition += 6;
  
  // Calculate micronutrients from items
  const oralMicro = {
    energy_kcal: record.oral_enteral_totals.energy_kcal,
    protein_g: record.oral_enteral_totals.protein_g,
    carbohydrate_g: record.oral_enteral_totals.carbohydrate_g,
    fat_g: record.oral_enteral_totals.fat_g,
    fiber_g: 0, sodium_mg: 0, potassium_mg: 0, chloride_mg: 0,
    omega3_mg: 0, omega6_mg: 0,
    vitamin_a_mcg: 0, vitamin_d3_mcg: 0, vitamin_e_mcg: 0, vitamin_k_mcg: 0,
    vitamin_b1_mg: 0, vitamin_b2_mg: 0, vitamin_b3_mg: 0, vitamin_b5_mg: 0,
    vitamin_b6_mg: 0, vitamin_b12_mcg: 0, folic_acid_mcg: 0, biotin_mcg: 0,
    choline_mg: 0, vitamin_c_mg: 0,
    calcium_mg: 0, phosphorus_mg: 0, magnesium_mg: 0, iron_mg: 0,
    iodine_mcg: 0, zinc_mg: 0, selenium_mcg: 0, manganese_mg: 0,
    fluoride_mg: 0, chromium_mcg: 0, copper_mg: 0
  };
  
  // Accumulate from oral items
  record.oral_enteral_items.forEach(item => {
    if (item.fiber_g !== undefined) oralMicro.fiber_g += item.fiber_g;
    if (item.sodium_mg !== undefined) oralMicro.sodium_mg += item.sodium_mg;
    if (item.potassium_mg !== undefined) oralMicro.potassium_mg += item.potassium_mg;
    if (item.chloride_mg !== undefined) oralMicro.chloride_mg += item.chloride_mg;
    if (item.omega3_mg !== undefined) oralMicro.omega3_mg += item.omega3_mg;
    if (item.omega6_mg !== undefined) oralMicro.omega6_mg += item.omega6_mg;
    if (item.vitamin_a_mcg !== undefined) oralMicro.vitamin_a_mcg += item.vitamin_a_mcg;
    if (item.vitamin_d3_mcg !== undefined) oralMicro.vitamin_d3_mcg += item.vitamin_d3_mcg;
    if (item.vitamin_e_mcg !== undefined) oralMicro.vitamin_e_mcg += item.vitamin_e_mcg;
    if (item.vitamin_k_mcg !== undefined) oralMicro.vitamin_k_mcg += item.vitamin_k_mcg;
    if (item.vitamin_b1_mg !== undefined) oralMicro.vitamin_b1_mg += item.vitamin_b1_mg;
    if (item.vitamin_b2_mg !== undefined) oralMicro.vitamin_b2_mg += item.vitamin_b2_mg;
    if (item.vitamin_b3_mg !== undefined) oralMicro.vitamin_b3_mg += item.vitamin_b3_mg;
    if (item.vitamin_b5_mg !== undefined) oralMicro.vitamin_b5_mg += item.vitamin_b5_mg;
    if (item.vitamin_b6_mg !== undefined) oralMicro.vitamin_b6_mg += item.vitamin_b6_mg;
    if (item.vitamin_b12_mcg !== undefined) oralMicro.vitamin_b12_mcg += item.vitamin_b12_mcg;
    if (item.folic_acid_mcg !== undefined) oralMicro.folic_acid_mcg += item.folic_acid_mcg;
    if (item.biotin_mcg !== undefined) oralMicro.biotin_mcg += item.biotin_mcg;
    if (item.choline_mg !== undefined) oralMicro.choline_mg += item.choline_mg;
    if (item.vitamin_c_mg !== undefined) oralMicro.vitamin_c_mg += item.vitamin_c_mg;
    if (item.calcium_mg !== undefined) oralMicro.calcium_mg += item.calcium_mg;
    if (item.phosphorus_mg !== undefined) oralMicro.phosphorus_mg += item.phosphorus_mg;
    if (item.magnesium_mg !== undefined) oralMicro.magnesium_mg += item.magnesium_mg;
    if (item.iron_mg !== undefined) oralMicro.iron_mg += item.iron_mg;
    if (item.iodine_mcg !== undefined) oralMicro.iodine_mcg += item.iodine_mcg;
    if (item.zinc_mg !== undefined) oralMicro.zinc_mg += item.zinc_mg;
    if (item.selenium_mcg !== undefined) oralMicro.selenium_mcg += item.selenium_mcg;
    if (item.manganese_mg !== undefined) oralMicro.manganese_mg += item.manganese_mg;
    if (item.fluoride_mg !== undefined) oralMicro.fluoride_mg += item.fluoride_mg;
    if (item.chromium_mcg !== undefined) oralMicro.chromium_mcg += item.chromium_mcg;
    if (item.copper_mg !== undefined) oralMicro.copper_mg += item.copper_mg;
  });
  
  const parenteralMicro = {
    energy_kcal: record.parenteral_totals.energy_kcal,
    protein_g: record.parenteral_totals.protein_g,
    carbohydrate_g: record.parenteral_totals.carbohydrate_g,
    fat_g: record.parenteral_totals.fat_g,
    sodium_mmol: 0, potassium_mmol: 0, calcium_mmol: 0, chloride_mmol: 0,
    magnesium_mmol: 0, phosphate_mmol: 0, lactate_mmol: 0, acetate_meq: 0, nitrogen_g: 0
  };
  
  // Accumulate from parenteral items
  record.parenteral_items.forEach(item => {
    if (item.sodium_mmol !== undefined) parenteralMicro.sodium_mmol += item.sodium_mmol;
    if (item.potassium_mmol !== undefined) parenteralMicro.potassium_mmol += item.potassium_mmol;
    if (item.calcium_mmol !== undefined) parenteralMicro.calcium_mmol += item.calcium_mmol;
    if (item.chloride_mmol !== undefined) parenteralMicro.chloride_mmol += item.chloride_mmol;
    if (item.magnesium_mmol !== undefined) parenteralMicro.magnesium_mmol += item.magnesium_mmol;
    if (item.phosphate_mmol !== undefined) parenteralMicro.phosphate_mmol += item.phosphate_mmol;
    if (item.lactate_mmol !== undefined) parenteralMicro.lactate_mmol += item.lactate_mmol;
    if (item.acetate_meq !== undefined) parenteralMicro.acetate_meq += item.acetate_meq;
    if (item.nitrogen_g !== undefined) parenteralMicro.nitrogen_g += item.nitrogen_g;
  });
  
  // Oral/Enteral Micronutrient Table
  const oralMicroData = [
    ['Energy', '(KKal)', oralMicro.energy_kcal.toFixed(2)],
    ['Protein', '(g)', oralMicro.protein_g.toFixed(2)],
    ['Carbohydrate', '(g)', oralMicro.carbohydrate_g.toFixed(2)],
    ['Fat', '(g)', oralMicro.fat_g.toFixed(2)],
    ['Sodium', '(mg)', oralMicro.sodium_mg.toFixed(2)],
    ['Potassium', '(mg)', oralMicro.potassium_mg.toFixed(2)],
    ['Fiber', '(g)', oralMicro.fiber_g.toFixed(2)],
    ['Omega 3', '(mg)', oralMicro.omega3_mg.toFixed(2)],
    ['Omega 6', '(mg)', oralMicro.omega6_mg.toFixed(2)],
    ['Vit A', '(mcg)', oralMicro.vitamin_a_mcg.toFixed(2)],
    ['Vit D3', '(mcg)', oralMicro.vitamin_d3_mcg.toFixed(2)],
    ['Vit E', '(mcg)', oralMicro.vitamin_e_mcg.toFixed(2)],
    ['Vit K', '(mcg)', oralMicro.vitamin_k_mcg.toFixed(2)],
    ['Vit B1', '(mg)', oralMicro.vitamin_b1_mg.toFixed(2)],
    ['Vit B2', '(mg)', oralMicro.vitamin_b2_mg.toFixed(2)],
    ['Vit B3', '(mg)', oralMicro.vitamin_b3_mg.toFixed(2)],
    ['Vit B5', '(mg)', oralMicro.vitamin_b5_mg.toFixed(2)],
    ['Vit B6', '(mg)', oralMicro.vitamin_b6_mg.toFixed(2)],
    ['Folic Acid', '(mcg)', oralMicro.folic_acid_mcg.toFixed(2)],
    ['Vit B12', '(mcg)', oralMicro.vitamin_b12_mcg.toFixed(2)],
    ['Biotin', '(mcg)', oralMicro.biotin_mcg.toFixed(2)],
    ['Choline', '(mg)', oralMicro.choline_mg.toFixed(2)],
    ['Vit C', '(mg)', oralMicro.vitamin_c_mg.toFixed(2)],
    ['Calcium', '(mg)', oralMicro.calcium_mg.toFixed(2)],
    ['Phosphorus', '(mg)', oralMicro.phosphorus_mg.toFixed(2)],
    ['Magnesium', '(mg)', oralMicro.magnesium_mg.toFixed(2)],
    ['Fe', '(mg)', oralMicro.iron_mg.toFixed(2)],
    ['Iodine', '(mcg)', oralMicro.iodine_mcg.toFixed(2)],
    ['Zinc', '(mg)', oralMicro.zinc_mg.toFixed(2)],
    ['Selenium', '(mcg)', oralMicro.selenium_mcg.toFixed(2)],
    ['Manganese', '(mg)', oralMicro.manganese_mg.toFixed(2)],
    ['Fluoride', '(mg)', oralMicro.fluoride_mg.toFixed(2)],
    ['Chromium', '(mcg)', oralMicro.chromium_mcg.toFixed(2)],
    ['Chloride', '(mg)', oralMicro.chloride_mg.toFixed(2)],
    ['Cu', '(mg)', oralMicro.copper_mg.toFixed(2)]
  ];
  
  // Parenteral Micronutrient Table  
  const parenteralMicroData = [
    ['Energy', '(KKal)', parenteralMicro.energy_kcal.toFixed(2)],
    ['Protein', '(g)', parenteralMicro.protein_g.toFixed(2)],
    ['Carbohydrate', '(g)', parenteralMicro.carbohydrate_g.toFixed(2)],
    ['Fat', '(g)', parenteralMicro.fat_g.toFixed(2)],
    ['Sodium', '(mmol)', parenteralMicro.sodium_mmol.toFixed(2)],
    ['Potassium', '(mmol)', parenteralMicro.potassium_mmol.toFixed(2)],
    ['Calcium', '(mmol)', parenteralMicro.calcium_mmol.toFixed(2)],
    ['Chloride', '(mmol)', parenteralMicro.chloride_mmol.toFixed(2)],
    ['Magnesium', '(mmol)', parenteralMicro.magnesium_mmol.toFixed(2)],
    ['Lactate', '(mmol)', parenteralMicro.lactate_mmol.toFixed(2)],
    ['Phosphate', '(mmol)', parenteralMicro.phosphate_mmol.toFixed(2)],
    ['Acetate', '(mEq)', parenteralMicro.acetate_meq.toFixed(2)],
    ['Nitrogen', '(g)', parenteralMicro.nitrogen_g.toFixed(2)]
  ];
  
  // Create Oral/Enteral table (LEFT SIDE)
  const leftMargin = 14;
  const tableWidth = 90; // Width for each table
  
  pdf.autoTable({
    startY: yPosition,
    head: [['ORAL/ENTERAL', '', '']],
    body: [['COMPOSITION', 'UNIT', 'AMOUNT'], ...oralMicroData],
    theme: 'grid',
    headStyles: {
      fillColor: [251, 191, 36], // Amber-400
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 9
    },
    bodyStyles: {
      fontSize: 7,
      cellPadding: 1.5
    },
    columnStyles: {
      0: { cellWidth: 35, halign: 'left' },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 30, halign: 'right' }
    },
    didParseCell: function(data) {
      // Header row for column names
      if (data.row.index === 0 && data.section === 'body') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [254, 243, 199]; // Amber-100
      }
      // Zebra striping
      if (data.row.index > 0 && data.section === 'body') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? [255, 255, 255] : [254, 243, 199];
      }
    },
    margin: { left: leftMargin, right: pageWidth - leftMargin - tableWidth }
  });
  
  // Create Parenteral table (RIGHT SIDE) - Start at same Y position
  const rightMargin = 14;
  
  pdf.autoTable({
    startY: yPosition, // Same starting Y position as Oral table
    head: [['PARENTERAL', '', '']],
    body: [['COMPOSITION', 'UNIT', 'AMOUNT'], ...parenteralMicroData],
    theme: 'grid',
    headStyles: {
      fillColor: [34, 211, 238], // Cyan-400
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 9
    },
    bodyStyles: {
      fontSize: 7,
      cellPadding: 1.5
    },
    columnStyles: {
      0: { cellWidth: 35, halign: 'left' },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 30, halign: 'right' }
    },
    didParseCell: function(data) {
      // Header row for column names
      if (data.row.index === 0 && data.section === 'body') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [207, 250, 254]; // Cyan-100
      }
      // Zebra striping
      if (data.row.index > 0 && data.section === 'body') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? [255, 255, 255] : [207, 250, 254];
      }
    },
    margin: { left: pageWidth / 2 + 3, right: rightMargin }
  });
  
  // ===== ADD PAGE NUMBERS TO ALL PAGES =====
  const totalPages = pdf.internal.getNumberOfPages();
  
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.setFont('helvetica', 'normal');
    
    // Footer with page number
    const footerText = `Page ${i} of ${totalPages}`;
    pdf.text(footerText, pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    // Optional: Add patient name in footer
    pdf.setFontSize(8);
    pdf.text(`${record.patient_name} - Daily Recall`, 14, pageHeight - 10);
    pdf.text(generateDate, pageWidth - 14, pageHeight - 10, { align: 'right' });
  }
  
  // Save PDF
  const fileName = `Food_Recall_${record.patient_name.replace(/\s+/g, '_')}_${new Date(record.date).toISOString().split('T')[0]}.pdf`;
  pdf.save(fileName);
};

// Food Database PDF Export
export const generateFoodPDF = async (foods) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  
  // Title
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Food Database Export', 14, 20);
  
  // Prepare table data
  const tableData = foods.map(food => [
    food.name,
    food.unit,
    Math.round(food.macronutrients.energy_kcal).toString(),
    food.macronutrients.protein_g.toFixed(1),
    food.macronutrients.carbohydrate_g.toFixed(1),
    food.macronutrients.fat_g.toFixed(1),
    food.is_custom ? 'Custom' : 'System'
  ]);
  
  pdf.autoTable({
    startY: 30,
    head: [['Food Name', 'Unit', 'Energy\n(kcal)', 'Protein\n(g)', 'Carbs\n(g)', 'Fat\n(g)', 'Type']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [144, 186, 144],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 9
    },
    bodyStyles: {
      fontSize: 8
    },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 25 },
      2: { cellWidth: 22, halign: 'right' },
      3: { cellWidth: 22, halign: 'right' },
      4: { cellWidth: 22, halign: 'right' },
      5: { cellWidth: 22, halign: 'right' },
      6: { cellWidth: 20, halign: 'center' }
    },
    margin: { left: 14, right: 14 }
  });
  
  pdf.save('Food_Database_Export.pdf');
};

// Parenteral Database PDF Export
export const generateParenteralPDF = async (products) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  
  // Title
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Parenteral Database Export', 14, 20);
  
  // Prepare table data
  const tableData = products.map(product => [
    product.name,
    product.unit,
    Math.round(product.macronutrients.energy_kcal).toString(),
    product.macronutrients.protein_g.toFixed(1),
    product.macronutrients.carbohydrate_g.toFixed(1),
    product.macronutrients.fat_g.toFixed(1),
    product.is_custom ? 'Custom' : 'System'
  ]);
  
  pdf.autoTable({
    startY: 30,
    head: [['Product Name', 'Unit', 'Energy\n(kcal)', 'Protein\n(g)', 'Carbs\n(g)', 'Fat\n(g)', 'Type']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [144, 186, 144],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 9
    },
    bodyStyles: {
      fontSize: 8
    },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 25 },
      2: { cellWidth: 22, halign: 'right' },
      3: { cellWidth: 22, halign: 'right' },
      4: { cellWidth: 22, halign: 'right' },
      5: { cellWidth: 22, halign: 'right' },
      6: { cellWidth: 20, halign: 'center' }
    },
    margin: { left: 14, right: 14 }
  });
  
  pdf.save('Parenteral_Database_Export.pdf');
};

export const generateCurrentCalculationPDF = async (
  patientOrName,
  currentDate,
  targetEnergy,
  oralItems,
  parenteralItems,
  oralTotals,
  parenteralTotals,
  grandTotal
) => {
  const patientObj = typeof patientOrName === 'object' && patientOrName !== null ? patientOrName : { name: patientOrName };
  const record = {
    patient: patientObj,
    patient_name: patientObj.name || 'Pasien',
    mr_number: patientObj.mr_number || patientObj.medical_record_number || '-',
    age: patientObj.age || '-',
    gender: patientObj.gender || '-',
    room: patientObj.room || patientObj.ward || '-',
    diagnosis: patientObj.diagnosis || '-',
    weight_kg: patientObj.weight_kg || patientObj.weight || '-',
    height_cm: patientObj.height_cm || patientObj.height || '-',
    date: currentDate,
    target_energy_kcal: targetEnergy,
    oral_enteral_items: oralItems,
    parenteral_items: parenteralItems,
    oral_enteral_totals: {
      energy_kcal: oralTotals.energy,
      protein_g: oralTotals.protein,
      carbohydrate_g: oralTotals.carbs,
      fat_g: oralTotals.fat
    },
    parenteral_totals: {
      energy_kcal: parenteralTotals.energy,
      protein_g: parenteralTotals.protein,
      carbohydrate_g: parenteralTotals.carbs,
      fat_g: parenteralTotals.fat
    },
    grand_totals: {
      energy_kcal: grandTotal.energy,
      protein_g: grandTotal.protein,
      carbohydrate_g: grandTotal.carbs,
      fat_g: grandTotal.fat
    }
  };
  
  await generatePDF(record);
};

// History PDF Export with detailed records and summary table
export const generateHistoryPDF = async (patient, records, graphsElementId) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let yPosition = 20;
  
  // Get current date for generation timestamp
  const generateDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const generateTime = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
  
  // ===== HEADER WITH GREEN BACKGROUND =====
  pdf.setFillColor(102, 221, 102); // Bright green matching screenshot
  pdf.rect(0, 0, pageWidth, 35, 'F');
  pdf.setTextColor(40, 40, 40);
  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  pdf.text('NUTRIMUS - Patient History Report', pageWidth / 2, 12, { align: 'center' });
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`${patient.name} - Last 7 Days`, pageWidth / 2, 21, { align: 'center' });
  
  // Patient details and generate date
  pdf.setFontSize(9);
  pdf.setTextColor(60, 60, 60);
  pdf.text(`Patient: ${patient.name} | Age: ${patient.age} years | Gender: ${patient.gender === 'male' ? 'Male' : 'Female'}`, pageWidth / 2, 28, { align: 'center' });
  pdf.text(`Generated: ${generateDate} at ${generateTime}`, pageWidth / 2, 32, { align: 'center' });
  
  pdf.setTextColor(0, 0, 0);
  yPosition = 42;
  
  // ===== GRAPHS SECTION - CAPTURE WITH BETTER QUALITY =====
  try {
    const graphsElement = document.getElementById(graphsElementId);
    if (graphsElement) {
      // Hide the export button and header title temporarily
      const exportButton = graphsElement.querySelector('button');
      const cardHeader = graphsElement.querySelector('.flex.flex-col.gap-3');
      
      if (exportButton) exportButton.style.display = 'none';
      if (cardHeader) {
        const titleEl = cardHeader.querySelector('div');
        if (titleEl) titleEl.style.display = 'none';
      }
      
      // Capture only the graphs grid
      const graphsGrid = graphsElement.querySelector('.grid.grid-cols-1.md\\:grid-cols-2');
      
      if (graphsGrid) {
        const canvas = await html2canvas(graphsGrid, {
          scale: 3, // Higher quality
          logging: false,
          backgroundColor: '#ffffff',
          useCORS: true,
          allowTaint: true
        });
        
        const imgData = canvas.toDataURL('image/png', 1.0);
        
        // Calculate dimensions to fit nicely on page
        const imgWidth = pageWidth - 20; // 10mm margin on each side
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Add to PDF starting below header
        const startY = 35;
        pdf.addImage(imgData, 'PNG', 10, startY, imgWidth, imgHeight);
        yPosition = startY + imgHeight + 10;
      }
      
      // Restore hidden elements
      if (exportButton) exportButton.style.display = '';
      if (cardHeader) {
        const titleEl = cardHeader.querySelector('div');
        if (titleEl) titleEl.style.display = '';
      }
    }
  } catch (error) {
    console.error('Error adding graphs to PDF:', error);
  }
  
  // ===== DETAILED RECORDS SECTION =====
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  
  if (yPosition > pageHeight - 40) {
    pdf.addPage();
    yPosition = 20;
  }
  
  pdf.text('Detailed Calculation Records', 14, yPosition);
  yPosition += 10;
  
  // Loop through each record and create detailed view
  records.forEach((record, index) => {
    // Check if we need a new page
    if (yPosition > pageHeight - 100) {
      pdf.addPage();
      yPosition = 20;
    }
    
    // Record header
    pdf.setFillColor(240, 240, 240);
    pdf.rect(14, yPosition - 5, pageWidth - 28, 8, 'F');
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    const recordDate = new Date(record.date).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    pdf.text(`Record ${index + 1} - ${recordDate}`, 16, yPosition);
    yPosition += 10;
    
    // Target Energy
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Target Energy: ${record.target_energy_kcal} Kcal`, 16, yPosition);
    yPosition += 8;
    
    // Oral/Enteral Items
    if (record.oral_enteral_items && record.oral_enteral_items.length > 0) {
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Oral/Enteral:', 16, yPosition);
      yPosition += 5;
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      record.oral_enteral_items.forEach(item => {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }
        const itemText = `  • ${item.name} (${item.quantity}) - ${Math.round(item.energy_kcal)} Kcal, ${item.protein_g.toFixed(1)}g P, ${item.carbohydrate_g.toFixed(1)}g C, ${item.fat_g.toFixed(1)}g F`;
        pdf.text(itemText, 18, yPosition);
        yPosition += 4;
      });
      yPosition += 2;
    }
    
    // Parenteral Items
    if (record.parenteral_items && record.parenteral_items.length > 0) {
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Parenteral:', 16, yPosition);
      yPosition += 5;
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      record.parenteral_items.forEach(item => {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }
        const itemText = `  • ${item.name} (${item.quantity}) - ${Math.round(item.energy_kcal)} Kcal, ${item.protein_g.toFixed(1)}g P, ${item.carbohydrate_g.toFixed(1)}g C, ${item.fat_g.toFixed(1)}g F`;
        pdf.text(itemText, 18, yPosition);
        yPosition += 4;
      });
      yPosition += 2;
    }
    
    // Totals
    const energy = record.grand_totals?.energy_kcal || record.grand_total?.energy_kcal || 0;
    const protein = record.grand_totals?.protein_g || record.grand_total?.protein_g || 0;
    const carbs = record.grand_totals?.carbohydrate_g || record.grand_total?.carbohydrate_g || 0;
    const fat = record.grand_totals?.fat_g || record.grand_total?.fat_g || 0;
    
    // Calculate Sodium and Potassium mEq
    let sodiumMeq = 0;
    let potassiumMeq = 0;
    
    if (record.oral_enteral_items) {
      record.oral_enteral_items.forEach(item => {
        if (item.sodium_mg) sodiumMeq += item.sodium_mg / 23;
        if (item.potassium_mg) potassiumMeq += item.potassium_mg / 39.1;
      });
    }
    
    if (record.parenteral_items) {
      record.parenteral_items.forEach(item => {
        if (item.sodium_mmol) sodiumMeq += item.sodium_mmol;
        if (item.potassium_mmol) potassiumMeq += item.potassium_mmol;
      });
    }
    
    const energyPct = ((energy / record.target_energy_kcal) * 100).toFixed(1);
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Total: ${Math.round(energy)} Kcal (${energyPct}%), ${protein.toFixed(1)}g P, ${carbs.toFixed(1)}g C, ${fat.toFixed(1)}g F`, 16, yPosition);
    yPosition += 5;
    pdf.text(`Sodium (Na): ${sodiumMeq.toFixed(1)} mEq, Potassium (K): ${potassiumMeq.toFixed(1)} mEq`, 16, yPosition);
    yPosition += 12;
  });
  
  // ===== SUMMARY TABLE (NEW) =====
  if (yPosition > pageHeight - 80) {
    pdf.addPage();
    yPosition = 20;
  }
  
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Summary Table', 14, yPosition);
  yPosition += 8;
  
  // Prepare table data
  const tableData = records.map(record => {
    const date = new Date(record.date).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
    
    const energy = record.grand_totals?.energy_kcal || record.grand_total?.energy_kcal || 0;
    const protein = record.grand_totals?.protein_g || record.grand_total?.protein_g || 0;
    const carbs = record.grand_totals?.carbohydrate_g || record.grand_total?.carbohydrate_g || 0;
    const fat = record.grand_totals?.fat_g || record.grand_total?.fat_g || 0;
    
    const energyPct = ((energy / record.target_energy_kcal) * 100).toFixed(1);
    
    // Calculate macronutrient percentages using dynamic factors
    // For history records, recalculate energy from items if available
    let proteinEnergy = protein * 4;
    let carbEnergy = 0;
    let fatEnergy = 0;
    
    // Try to use dynamic factors from parenteral items if available
    if (record.parenteral_items && record.parenteral_items.length > 0) {
      // Parenteral: use dynamic factors
      record.parenteral_items.forEach(item => {
        carbEnergy += item.carbohydrate_g * (item.carb_factor || 4.0);
        fatEnergy += item.fat_g * (item.fat_factor || 9.0);
      });
      // Add oral items with static factors
      if (record.oral_enteral_items) {
        record.oral_enteral_items.forEach(item => {
          carbEnergy += item.carbohydrate_g * 4;
          fatEnergy += item.fat_g * 9;
        });
      }
    } else {
      // If no items available (old records), use static factors
      carbEnergy = carbs * 4;
      fatEnergy = fat * 9;
    }
    
    const totalEnergy = proteinEnergy + carbEnergy + fatEnergy;
    const proteinPct = totalEnergy > 0 ? ((proteinEnergy / totalEnergy) * 100).toFixed(1) : '0.0';
    const carbsPct = totalEnergy > 0 ? ((carbEnergy / totalEnergy) * 100).toFixed(1) : '0.0';
    const fatPct = totalEnergy > 0 ? ((fatEnergy / totalEnergy) * 100).toFixed(1) : '0.0';
    
    return [
      date,
      record.target_energy_kcal.toString(),
      `${Math.round(energy)}\n(${energyPct}%)`,
      `${protein.toFixed(1)}\n(${proteinPct}%)`,
      `${carbs.toFixed(1)}\n(${carbsPct}%)`,
      `${fat.toFixed(1)}\n(${fatPct}%)`
    ];
  });
  
  pdf.autoTable({
    startY: yPosition,
    head: [['Date', 'Target\nKcal', 'Energy\n(kcal)', 'Protein\n(g)', 'Carbs\n(g)', 'Fat\n(g)']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [144, 186, 144],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 9
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 2
    },
    columnStyles: {
      0: { cellWidth: 30, halign: 'center' },
      1: { cellWidth: 25, halign: 'center' },
      2: { cellWidth: 30, halign: 'center' },
      3: { cellWidth: 25, halign: 'center' },
      4: { cellWidth: 25, halign: 'center' },
      5: { cellWidth: 25, halign: 'center' }
    },
    margin: { left: 14, right: 14 }
  });
  
  // ===== MICRONUTRIENT SUMMARY (AGGREGATED FROM ALL RECORDS) =====
  // ===== MICRONUTRIENT SUMMARY - ALWAYS NEW PAGE =====
  pdf.addPage();
  yPosition = 20;
  
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Total Komposisi Macronutrient & Micronutrient (Latest Record)', 14, yPosition);
  yPosition += 10;
  
  // Use latest record for micronutrient display
  const latestRecord = records[records.length - 1];
  
  if (latestRecord) {
    // Calculate micronutrients from latest record items (FULL LIST - same as daily PDF)
    const oralMicro = {
      energy_kcal: latestRecord.oral_enteral_totals?.energy_kcal || 0,
      protein_g: latestRecord.oral_enteral_totals?.protein_g || 0,
      carbohydrate_g: latestRecord.oral_enteral_totals?.carbohydrate_g || 0,
      fat_g: latestRecord.oral_enteral_totals?.fat_g || 0,
      fiber_g: 0, sodium_mg: 0, potassium_mg: 0, chloride_mg: 0,
      omega3_mg: 0, omega6_mg: 0,
      vitamin_a_mcg: 0, vitamin_d3_mcg: 0, vitamin_e_mcg: 0, vitamin_k_mcg: 0,
      vitamin_b1_mg: 0, vitamin_b2_mg: 0, vitamin_b3_mg: 0, vitamin_b5_mg: 0,
      vitamin_b6_mg: 0, vitamin_b12_mcg: 0, folic_acid_mcg: 0, biotin_mcg: 0,
      choline_mg: 0, vitamin_c_mg: 0,
      calcium_mg: 0, phosphorus_mg: 0, magnesium_mg: 0, iron_mg: 0,
      iodine_mcg: 0, zinc_mg: 0, selenium_mcg: 0, manganese_mg: 0,
      fluoride_mg: 0, chromium_mcg: 0, copper_mg: 0
    };
    
    // Accumulate from oral items
    if (latestRecord.oral_enteral_items) {
      latestRecord.oral_enteral_items.forEach(item => {
        if (item.fiber_g !== undefined) oralMicro.fiber_g += item.fiber_g;
        if (item.sodium_mg !== undefined) oralMicro.sodium_mg += item.sodium_mg;
        if (item.potassium_mg !== undefined) oralMicro.potassium_mg += item.potassium_mg;
        if (item.chloride_mg !== undefined) oralMicro.chloride_mg += item.chloride_mg;
        if (item.omega3_mg !== undefined) oralMicro.omega3_mg += item.omega3_mg;
        if (item.omega6_mg !== undefined) oralMicro.omega6_mg += item.omega6_mg;
        if (item.vitamin_a_mcg !== undefined) oralMicro.vitamin_a_mcg += item.vitamin_a_mcg;
        if (item.vitamin_d3_mcg !== undefined) oralMicro.vitamin_d3_mcg += item.vitamin_d3_mcg;
        if (item.vitamin_e_mcg !== undefined) oralMicro.vitamin_e_mcg += item.vitamin_e_mcg;
        if (item.vitamin_k_mcg !== undefined) oralMicro.vitamin_k_mcg += item.vitamin_k_mcg;
        if (item.vitamin_b1_mg !== undefined) oralMicro.vitamin_b1_mg += item.vitamin_b1_mg;
        if (item.vitamin_b2_mg !== undefined) oralMicro.vitamin_b2_mg += item.vitamin_b2_mg;
        if (item.vitamin_b3_mg !== undefined) oralMicro.vitamin_b3_mg += item.vitamin_b3_mg;
        if (item.vitamin_b5_mg !== undefined) oralMicro.vitamin_b5_mg += item.vitamin_b5_mg;
        if (item.vitamin_b6_mg !== undefined) oralMicro.vitamin_b6_mg += item.vitamin_b6_mg;
        if (item.vitamin_b12_mcg !== undefined) oralMicro.vitamin_b12_mcg += item.vitamin_b12_mcg;
        if (item.folic_acid_mcg !== undefined) oralMicro.folic_acid_mcg += item.folic_acid_mcg;
        if (item.biotin_mcg !== undefined) oralMicro.biotin_mcg += item.biotin_mcg;
        if (item.choline_mg !== undefined) oralMicro.choline_mg += item.choline_mg;
        if (item.vitamin_c_mg !== undefined) oralMicro.vitamin_c_mg += item.vitamin_c_mg;
        if (item.calcium_mg !== undefined) oralMicro.calcium_mg += item.calcium_mg;
        if (item.phosphorus_mg !== undefined) oralMicro.phosphorus_mg += item.phosphorus_mg;
        if (item.magnesium_mg !== undefined) oralMicro.magnesium_mg += item.magnesium_mg;
        if (item.iron_mg !== undefined) oralMicro.iron_mg += item.iron_mg;
        if (item.iodine_mcg !== undefined) oralMicro.iodine_mcg += item.iodine_mcg;
        if (item.zinc_mg !== undefined) oralMicro.zinc_mg += item.zinc_mg;
        if (item.selenium_mcg !== undefined) oralMicro.selenium_mcg += item.selenium_mcg;
        if (item.manganese_mg !== undefined) oralMicro.manganese_mg += item.manganese_mg;
        if (item.fluoride_mg !== undefined) oralMicro.fluoride_mg += item.fluoride_mg;
        if (item.chromium_mcg !== undefined) oralMicro.chromium_mcg += item.chromium_mcg;
        if (item.copper_mg !== undefined) oralMicro.copper_mg += item.copper_mg;
      });
    }
    
    const parenteralMicro = {
      energy_kcal: latestRecord.parenteral_totals?.energy_kcal || 0,
      protein_g: latestRecord.parenteral_totals?.protein_g || 0,
      carbohydrate_g: latestRecord.parenteral_totals?.carbohydrate_g || 0,
      fat_g: latestRecord.parenteral_totals?.fat_g || 0,
      sodium_mmol: 0, potassium_mmol: 0, calcium_mmol: 0, chloride_mmol: 0,
      magnesium_mmol: 0, phosphate_mmol: 0, lactate_mmol: 0, acetate_meq: 0, nitrogen_g: 0
    };
    
    // Accumulate from parenteral items
    if (latestRecord.parenteral_items) {
      latestRecord.parenteral_items.forEach(item => {
        if (item.sodium_mmol !== undefined) parenteralMicro.sodium_mmol += item.sodium_mmol;
        if (item.potassium_mmol !== undefined) parenteralMicro.potassium_mmol += item.potassium_mmol;
        if (item.calcium_mmol !== undefined) parenteralMicro.calcium_mmol += item.calcium_mmol;
        if (item.chloride_mmol !== undefined) parenteralMicro.chloride_mmol += item.chloride_mmol;
        if (item.magnesium_mmol !== undefined) parenteralMicro.magnesium_mmol += item.magnesium_mmol;
        if (item.phosphate_mmol !== undefined) parenteralMicro.phosphate_mmol += item.phosphate_mmol;
        if (item.lactate_mmol !== undefined) parenteralMicro.lactate_mmol += item.lactate_mmol;
        if (item.acetate_meq !== undefined) parenteralMicro.acetate_meq += item.acetate_meq;
        if (item.nitrogen_g !== undefined) parenteralMicro.nitrogen_g += item.nitrogen_g;
      });
    }
    
    // FULL Oral/Enteral Micronutrient Table (35 rows - same as daily PDF)
    const oralMicroData = [
      ['Energy', '(KKal)', oralMicro.energy_kcal.toFixed(2)],
      ['Protein', '(g)', oralMicro.protein_g.toFixed(2)],
      ['Carbohydrate', '(g)', oralMicro.carbohydrate_g.toFixed(2)],
      ['Fat', '(g)', oralMicro.fat_g.toFixed(2)],
      ['Sodium', '(mg)', oralMicro.sodium_mg.toFixed(2)],
      ['Potassium', '(mg)', oralMicro.potassium_mg.toFixed(2)],
      ['Fiber', '(g)', oralMicro.fiber_g.toFixed(2)],
      ['Omega 3', '(mg)', oralMicro.omega3_mg.toFixed(2)],
      ['Omega 6', '(mg)', oralMicro.omega6_mg.toFixed(2)],
      ['Vit A', '(mcg)', oralMicro.vitamin_a_mcg.toFixed(2)],
      ['Vit D3', '(mcg)', oralMicro.vitamin_d3_mcg.toFixed(2)],
      ['Vit E', '(mcg)', oralMicro.vitamin_e_mcg.toFixed(2)],
      ['Vit K', '(mcg)', oralMicro.vitamin_k_mcg.toFixed(2)],
      ['Vit B1', '(mg)', oralMicro.vitamin_b1_mg.toFixed(2)],
      ['Vit B2', '(mg)', oralMicro.vitamin_b2_mg.toFixed(2)],
      ['Vit B3', '(mg)', oralMicro.vitamin_b3_mg.toFixed(2)],
      ['Vit B5', '(mg)', oralMicro.vitamin_b5_mg.toFixed(2)],
      ['Vit B6', '(mg)', oralMicro.vitamin_b6_mg.toFixed(2)],
      ['Folic Acid', '(mcg)', oralMicro.folic_acid_mcg.toFixed(2)],
      ['Vit B12', '(mcg)', oralMicro.vitamin_b12_mcg.toFixed(2)],
      ['Biotin', '(mcg)', oralMicro.biotin_mcg.toFixed(2)],
      ['Choline', '(mg)', oralMicro.choline_mg.toFixed(2)],
      ['Vit C', '(mg)', oralMicro.vitamin_c_mg.toFixed(2)],
      ['Calcium', '(mg)', oralMicro.calcium_mg.toFixed(2)],
      ['Phosphorus', '(mg)', oralMicro.phosphorus_mg.toFixed(2)],
      ['Magnesium', '(mg)', oralMicro.magnesium_mg.toFixed(2)],
      ['Fe', '(mg)', oralMicro.iron_mg.toFixed(2)],
      ['Iodine', '(mcg)', oralMicro.iodine_mcg.toFixed(2)],
      ['Zinc', '(mg)', oralMicro.zinc_mg.toFixed(2)],
      ['Selenium', '(mcg)', oralMicro.selenium_mcg.toFixed(2)],
      ['Manganese', '(mg)', oralMicro.manganese_mg.toFixed(2)],
      ['Fluoride', '(mg)', oralMicro.fluoride_mg.toFixed(2)],
      ['Chromium', '(mcg)', oralMicro.chromium_mcg.toFixed(2)],
      ['Chloride', '(mg)', oralMicro.chloride_mg.toFixed(2)],
      ['Cu', '(mg)', oralMicro.copper_mg.toFixed(2)]
    ];
    
    // FULL Parenteral Micronutrient Table (13 rows - same as daily PDF)
    const parenteralMicroData = [
      ['Energy', '(KKal)', parenteralMicro.energy_kcal.toFixed(2)],
      ['Protein', '(g)', parenteralMicro.protein_g.toFixed(2)],
      ['Carbohydrate', '(g)', parenteralMicro.carbohydrate_g.toFixed(2)],
      ['Fat', '(g)', parenteralMicro.fat_g.toFixed(2)],
      ['Sodium', '(mmol)', parenteralMicro.sodium_mmol.toFixed(2)],
      ['Potassium', '(mmol)', parenteralMicro.potassium_mmol.toFixed(2)],
      ['Calcium', '(mmol)', parenteralMicro.calcium_mmol.toFixed(2)],
      ['Chloride', '(mmol)', parenteralMicro.chloride_mmol.toFixed(2)],
      ['Magnesium', '(mmol)', parenteralMicro.magnesium_mmol.toFixed(2)],
      ['Lactate', '(mmol)', parenteralMicro.lactate_mmol.toFixed(2)],
      ['Phosphate', '(mmol)', parenteralMicro.phosphate_mmol.toFixed(2)],
      ['Acetate', '(mEq)', parenteralMicro.acetate_meq.toFixed(2)],
      ['Nitrogen', '(g)', parenteralMicro.nitrogen_g.toFixed(2)]
    ];
    
    // Create Oral/Enteral table (LEFT SIDE) - FULL VERSION
    const leftMargin = 14;
    const tableWidth = 90;
    
    pdf.autoTable({
      startY: yPosition,
      head: [['ORAL/ENTERAL', '', '']],
      body: [['COMPOSITION', 'UNIT', 'AMOUNT'], ...oralMicroData],
      theme: 'grid',
      headStyles: {
        fillColor: [251, 191, 36],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 9
      },
      bodyStyles: {
        fontSize: 7,
        cellPadding: 1.5
      },
      columnStyles: {
        0: { cellWidth: 35, halign: 'left' },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 30, halign: 'right' }
      },
      didParseCell: function(data) {
        if (data.row.index === 0 && data.section === 'body') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [254, 243, 199];
        }
        if (data.row.index > 0 && data.section === 'body') {
          data.cell.styles.fillColor = data.row.index % 2 === 0 ? [255, 255, 255] : [254, 243, 199];
        }
      },
      margin: { left: leftMargin, right: pageWidth - leftMargin - tableWidth }
    });
    
    // Parenteral table (RIGHT SIDE) - FULL VERSION
    const rightMargin = 14;
    
    pdf.autoTable({
      startY: yPosition,
      head: [['PARENTERAL', '', '']],
      body: [['COMPOSITION', 'UNIT', 'AMOUNT'], ...parenteralMicroData],
      theme: 'grid',
      headStyles: {
        fillColor: [34, 211, 238],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 9
      },
      bodyStyles: {
        fontSize: 7,
        cellPadding: 1.5
      },
      columnStyles: {
        0: { cellWidth: 35, halign: 'left' },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 30, halign: 'right' }
      },
      didParseCell: function(data) {
        if (data.row.index === 0 && data.section === 'body') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [207, 250, 254];
        }
        if (data.row.index > 0 && data.section === 'body') {
          data.cell.styles.fillColor = data.row.index % 2 === 0 ? [255, 255, 255] : [207, 250, 254];
        }
      },
      margin: { left: pageWidth / 2 + 3, right: rightMargin }
    });
  }
  
  // ===== ADD PAGE NUMBERS TO ALL PAGES =====
  const totalPages = pdf.internal.getNumberOfPages();
  
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.setFont('helvetica', 'normal');
    
    // Footer with page number
    const footerText = `Page ${i} of ${totalPages}`;
    pdf.text(footerText, pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    // Optional: Add patient name in footer
    pdf.setFontSize(8);
    pdf.text(`${patient.name} - History Report`, 14, pageHeight - 10);
    pdf.text(generateDate, pageWidth - 14, pageHeight - 10, { align: 'right' });
  }
  
  // Save PDF
  const fileName = `Patient_History_${patient.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(fileName);
};

